import { useState, type SyntheticEvent } from "react";

import type { components } from "../../api/schema";
import { requestErrorDescription } from "../../lib/errors";
import { Button, Inline, InlineError, Stack, Text, TextInput } from "../../ui";
import {
  OrderedScenarioMembersEditor,
  type ScenarioMemberOption,
} from "./OrderedScenarioMembersEditor";

type ScenarioSetResponse = components["schemas"]["ScenarioSetResponse"];

export interface ScenarioSetFormSubmission {
  displayName: string;
  scenarioIds: string[];
}

export interface ScenarioSetEditorProps {
  error: string | null;
  hasNextScenarioPage: boolean;
  isFetchingNextScenarioPage: boolean;
  isPending: boolean;
  onCancel: () => void;
  onLoadMoreScenarios: () => void;
  onSubmit: (submission: ScenarioSetFormSubmission) => Promise<void>;
  scenarioOptions: ScenarioMemberOption[];
  scenarioOptionsError?: string;
  scenarioSet: ScenarioSetResponse | null;
}

function memberScenarioIds(scenarioSet: ScenarioSetResponse | null): string[] {
  return [...(scenarioSet?.members ?? [])]
    .sort((left, right) => left.sort_order - right.sort_order)
    .map((member) => member.scenario_id);
}

export function ScenarioSetEditor(props: ScenarioSetEditorProps) {
  return (
    <ScenarioSetEditorForm key={props.scenarioSet?.id ?? "new"} {...props} />
  );
}

function ScenarioSetEditorForm({
  error,
  hasNextScenarioPage,
  isFetchingNextScenarioPage,
  isPending,
  onCancel,
  onLoadMoreScenarios,
  onSubmit,
  scenarioOptions,
  scenarioOptionsError,
  scenarioSet,
}: ScenarioSetEditorProps) {
  const [displayName, setDisplayName] = useState(
    scenarioSet?.display_name ?? "",
  );
  const [scenarioIds, setScenarioIds] = useState(() =>
    memberScenarioIds(scenarioSet),
  );
  const [localError, setLocalError] = useState<string | null>(null);

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextDisplayName = displayName.trim();
    if (nextDisplayName.length === 0) {
      setLocalError("Display name is required.");
      return;
    }
    setLocalError(null);
    try {
      await onSubmit({ displayName: nextDisplayName, scenarioIds });
    } catch (caught) {
      setLocalError(requestErrorDescription(caught));
    }
  }

  return (
    <Stack gap={4}>
      <Text variant="caps">
        {scenarioSet ? "Edit scenario set" : "Create scenario set"}
      </Text>
      <form
        onSubmit={(event) => {
          void handleSubmit(event);
        }}
      >
        <Stack gap={4}>
          <TextInput
            label="Display name"
            required
            value={displayName}
            onChange={(event) => {
              setDisplayName(event.currentTarget.value);
            }}
          />
          <OrderedScenarioMembersEditor
            error={scenarioOptionsError}
            hasNextPage={hasNextScenarioPage}
            isFetchingNextPage={isFetchingNextScenarioPage}
            options={scenarioOptions}
            value={scenarioIds}
            onLoadMore={onLoadMoreScenarios}
            onChange={setScenarioIds}
          />
          {(localError ?? error) ? (
            <InlineError>{localError ?? error}</InlineError>
          ) : null}
          <Inline>
            <Button disabled={isPending} type="submit">
              {isPending ? "Saving..." : "Save"}
            </Button>
            <Button
              disabled={isPending}
              type="button"
              variant="secondary"
              onClick={onCancel}
            >
              Cancel
            </Button>
          </Inline>
        </Stack>
      </form>
    </Stack>
  );
}
